import { useCallback, useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Button, Center, Drawer, Loader, ScrollArea, Select, Stack, Text, Textarea, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconBulb, IconChevronRight, IconCheck, IconChevronDown, IconChevronUp, IconClock, IconEye, IconFileText, IconHistory, IconLink, IconMail, IconMessage, IconPrinter, IconRefresh, IconSend, IconTool, IconUrgent } from '@tabler/icons-react';
import useSWR from 'swr';
import { conversationsApi, inspectionApi, shopApi } from '../api/client';
import { getBackendUrl } from '../../utils/backendConfig';
import type { EmailTemplate, MessageResponse } from '../types';

const BORDER = '#27272A';
const modalInputStyles = { label: { color: '#A1A1AA', fontSize: 12, marginBottom: 4 }, input: { background: '#27272A', border: `1px solid ${BORDER}`, color: '#fff' } };
const modalSelectStyles = {
  label: { color: '#A1A1AA', fontSize: 12, marginBottom: 4 },
  input: { background: '#27272A', border: `1px solid ${BORDER}`, color: '#fff' },
  dropdown: { background: '#27272A', border: `1px solid ${BORDER}` },
  option: { color: '#fff', '&[data-selected]': { background: '#3B82F6' }, '&[data-hovered]': { background: '#3F3F46' } },
};

function CloseTab({ onClick }: { onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      style={{
        position: 'absolute',
        left: -14,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 20,
        width: 28,
        height: 48,
        background: '#1E1E21',
        border: `1px solid ${BORDER}`,
        borderRadius: '8px 0 0 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.background = '#2A2A2E'; }}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.background = '#1E1E21'; }}
    >
      <IconChevronRight size={14} color="#A1A1AA" />
    </Box>
  );
}

function renderVariables(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] || `{{${key}}}`);
}

const REPORT_EVENT_PREFIXES = [
  'Customer opened',
  'Customer clicked',
  'Customer asked',
  'Customer requested',
  'Customer submitted',
  'Customer wants to proceed',
  'Customer interacted',
];

function isReportEvent(msg: MessageResponse): boolean {
  if (msg.role !== 'assistant') return false;
  return REPORT_EVENT_PREFIXES.some((p) => msg.content.startsWith(p)) || msg.content.includes('**Customer wants to proceed');
}


function formatTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function activityEventIcon(content: string): React.ReactNode {
  const c = content.toLowerCase();
  if (c.includes('wants to proceed') || c.includes('approved')) return <IconCheck size={12} color="#22C55E" />;
  if (c.includes('declined')) return <span style={{ fontSize: 11, lineHeight: 1 }}>&#10060;</span>;
  if (c.includes('submitted a review') || c.includes('review')) return <span style={{ fontSize: 11, lineHeight: 1 }}>&#11088;</span>;
  if (c.includes('asked a question') || c.includes('question')) return <span style={{ fontSize: 11, lineHeight: 1 }}>&#10067;</span>;
  if (c.includes('personalized') || c.includes('plan') || c.includes('preferences')) return <IconTool size={12} color="#F59E0B" />;
  if (c.includes('opened the report') || c.includes('viewed')) return <IconEye size={12} color="#3B82F6" />;
  if (c.includes('clicked') || c.includes('interacted')) return <IconEye size={12} color="#71717A" />;
  return <IconEye size={12} color="#71717A" />;
}

function CustomerActivityPanel({ messages }: { messages?: MessageResponse[] }) {
  const events = (messages || []).filter(isReportEvent);
  const [collapsed, setCollapsed] = useState(false);

  if (!events.length) return null;

  return (
    <Box style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#1E1E21', borderTop: `1px solid ${BORDER}` }}>
      {/* Collapsible header */}
      <UnstyledButton
        onClick={() => setCollapsed((v) => !v)}
        style={{
          padding: '8px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#1E1E21', flexShrink: 0,
        }}
      >
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text size="11px" fw={700} c="#E4E4E7" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>Customer Activity</Text>
          <Box style={{
            background: '#3B82F6', borderRadius: 10, padding: '1px 7px',
            fontSize: 10, fontWeight: 700, color: '#fff', lineHeight: '16px',
          }}>
            {events.length}
          </Box>
        </Box>
        {collapsed ? <IconChevronUp size={14} color="#71717A" /> : <IconChevronDown size={14} color="#71717A" />}
      </UnstyledButton>
      {/* Event list */}
      {!collapsed && (
        <ScrollArea style={{ maxHeight: 200 }}>
          <Box style={{ paddingBottom: 8 }}>
            {events.map((ev, idx) => (
              <Box key={ev.id} style={{ padding: '5px 16px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                {/* Timeline dot + line */}
                <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 16, paddingTop: 3 }}>
                  <Box style={{
                    width: 18, height: 18, borderRadius: '50%',
                    background: '#27272A', border: `1px solid ${BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {activityEventIcon(ev.content)}
                  </Box>
                  {idx < events.length - 1 && (
                    <Box style={{ width: 1, flex: 1, minHeight: 12, background: '#3F3F46', marginTop: 2 }} />
                  )}
                </Box>
                <Box style={{ flex: 1, minWidth: 0, paddingBottom: idx < events.length - 1 ? 4 : 0 }}>
                  <Text size="12px" c="#D4D4D8" lineClamp={2} style={{ lineHeight: 1.4 }}>
                    {ev.content.replace(/\*\*/g, '').split('\n')[0]}
                  </Text>
                  <Text size="11px" c="#52525B" mt={2}>{formatTime(ev.created_at)}</Text>
                </Box>
              </Box>
            ))}
          </Box>
        </ScrollArea>
      )}
    </Box>
  );
}

export interface ReportActions {
  handleShare: () => void;
  handlePrint: () => void;
  openEmailModal: () => void;
  openSmsModal: () => void;
  copied: boolean;
  hasReport: boolean;
}

interface ReportPreviewPanelProps {
  inspectionId: number | undefined;
  lastChangedAt?: number;
  onClose?: () => void;
  onGenerateReport?: () => void;
  generating?: boolean;
  vehicleEmail?: string | null;
  vehiclePhone?: string | null;
  vehicleDescription?: string | null;
  shopName?: string | null;
  messages?: MessageResponse[];
  conversationId?: number;
  hideActionBar?: boolean;
  actionsRef?: React.MutableRefObject<ReportActions | null>;
}

export function ReportPreviewPanel({
  inspectionId,
  lastChangedAt,
  onClose,
  onGenerateReport,
  generating,
  vehicleEmail,
  vehiclePhone,
  vehicleDescription,
  shopName,
  messages: allMessages,
  conversationId,
  hideActionBar,
  actionsRef,
}: ReportPreviewPanelProps) {
  // Use the same portal URL as customers see — one renderer for everything
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!conversationId) return;
    conversationsApi.getReportUrl(conversationId).then((r) => {
      // Build portal URL from current origin (works on localhost + prod)
      const token = new URL(r.portal_url).searchParams.get('token') || '';
      const baseUrl = `${window.location.origin}/portal/${conversationId}?token=${token}`;
      conversationsApi.get(conversationId).then((conv) => {
        const stage = conv.workflow_stage || 'registration';
        const stageMap: Record<string, string> = { registration: 'intake', inspection: 'inspection', quote: 'estimate', completed: 'complete' };
        setPortalUrl(`${baseUrl}&stage=${stageMap[stage] || stage}`);
      }).catch(() => setPortalUrl(baseUrl));
    }).catch(() => {});
  }, [conversationId, refreshKey]);

  // Debounced refresh when data changes
  useEffect(() => {
    if (!lastChangedAt) return;
    const t = setTimeout(() => setRefreshKey((k) => k + 1), 800);
    return () => clearTimeout(t);
  }, [lastChangedAt]);

  const data = portalUrl ? { html: portalUrl } : null;
  const isLoading = !portalUrl && !!conversationId;
  const isValidating = false;
  const error = null;
  const mutate = () => setRefreshKey((k) => k + 1);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeOverlayRef = useRef<HTMLDivElement>(null);
  const updating = false;
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  // Email modal state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailBody, setEmailBody] = useState('');
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  const templateVars: Record<string, string> = {
    customer_name: 'Customer',
    vehicle: vehicleDescription || 'your vehicle',
    shop_name: shopName || 'Our Shop',
    report_url: '#',
    shop_phone: '',
    shop_email: '',
  };

  const DEFAULT_TEMPLATES: EmailTemplate[] = [
    {
      id: '__default_report',
      name: 'Service Report Ready',
      subject: '{{shop_name}} - Your {{vehicle}} Service Report',
      body: 'Hi {{customer_name}},\n\nYour {{vehicle}} service report is ready for review.\n\nThe report includes our findings, condition photos where available, and recommended next steps with estimated costs.\n\nIf you have any questions, don\'t hesitate to give us a call.\n\nThank you for choosing {{shop_name}}!',
    },
    {
      id: '__default_followup',
      name: 'Follow-up Reminder',
      subject: '{{shop_name}} - Service Follow-up for {{vehicle}}',
      body: 'Hi {{customer_name}},\n\nWe wanted to follow up on your recent {{vehicle}} service report.\n\nThere are a few recommended items we\'d love to help you take care of before they become bigger issues. You can review everything in your report using the link below.\n\nFeel free to reach out anytime to schedule — we\'re happy to help.\n\nBest regards,\n{{shop_name}}',
    },
  ];

  const allTemplates = emailTemplates.length > 0 ? emailTemplates : DEFAULT_TEMPLATES;
  const selectedTemplate = allTemplates.find((t) => t.id === selectedTemplateId) || null;

  const [printing, setPrinting] = useState(false);
  const handlePrint = useCallback(async () => {
    if (!inspectionId || printing) return;
    setPrinting(true);
    try {
      const { html } = await inspectionApi.printReport(inspectionId);
      const win = window.open('', '_blank');
      if (!win) return;
      win.document.write(html);
      win.document.close();
      win.addEventListener('load', () => win.print());
      setTimeout(() => win.print(), 500);
    } catch (e) {
      console.error('[PRINT] Failed to fetch print report', e);
    } finally {
      setPrinting(false);
    }
  }, [inspectionId, printing]);

  const handleShare = useCallback(async () => {
    if (!inspectionId) return;
    setSharing(true);
    try {
      const { url } = await inspectionApi.shareReport(inspectionId);
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      if (portalUrl) window.open(portalUrl, '_blank');
    } finally {
      setSharing(false);
    }
  }, [inspectionId, portalUrl]);

  const defaultSubject = vehicleDescription ? `Service Report - ${vehicleDescription}` : 'Service Report';

  const applyTemplate = useCallback((tpl: EmailTemplate) => {
    setSelectedTemplateId(tpl.id);
    setEmailSubject(renderVariables(tpl.subject, templateVars));
    setEmailBody(renderVariables(tpl.body, templateVars));
  }, [templateVars]);

  const [smsOpen, setSmsOpen] = useState(false);
  const [smsTo, setSmsTo] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);

  const openSmsModal = useCallback(async () => {
    setSmsTo(vehiclePhone || '');
    setSmsSent(false);
    setSmsOpen(true);
    const name = vehicleDescription || 'your vehicle';
    const shop = shopName || 'Our shop';
    setSmsBody(`Hi, your service report from ${shop} for ${name} is ready. Generating link...`);
    try {
      if (inspectionId) {
        const { url } = await inspectionApi.shareReport(inspectionId);
        setSmsBody(`Hi, your service report from ${shop} for ${name} is ready. View it here: ${url}`);
      }
    } catch {
      setSmsBody(`Hi, your service report from ${shop} for ${name} is ready. View it here: [link will be included on send]`);
    }
  }, [vehiclePhone, vehicleDescription, shopName, inspectionId]);

  const handleSmsSend = useCallback(async () => {
    if (!inspectionId || !smsTo.trim()) return;
    setSmsSending(true);
    try {
      await inspectionApi.smsReport(inspectionId, smsTo.trim());
      setSmsSent(true);
    } catch (e) {
      console.error('[SMS_REPORT] failed:', e);
    } finally {
      setSmsSending(false);
    }
  }, [inspectionId, smsTo]);

  const openEmailModal = useCallback(async () => {
    setEmailTo(vehicleEmail || '');
    setEmailSent(false);
    setEmailOpen(true);

    if (!templatesLoaded) {
      try {
        const shop = await shopApi.get();
        const tpls = shop.email_templates || [];
        setEmailTemplates(tpls);
        const available = tpls.length > 0 ? tpls : DEFAULT_TEMPLATES;
        const def = available.find((t) => t.is_default) || available[0];
        applyTemplate(def);
      } catch {
        applyTemplate(DEFAULT_TEMPLATES[0]);
      }
      setTemplatesLoaded(true);
    } else if (selectedTemplate) {
      applyTemplate(selectedTemplate);
    } else {
      setEmailSubject(defaultSubject);
      setEmailBody(renderVariables(DEFAULT_TEMPLATES[0].body, templateVars));
    }
  }, [vehicleEmail, templatesLoaded, selectedTemplate, templateVars, defaultSubject, applyTemplate]);

  const onTemplateChange = useCallback((val: string | null) => {
    if (val) {
      const tpl = allTemplates.find((t) => t.id === val);
      if (tpl) applyTemplate(tpl);
    } else {
      setSelectedTemplateId(null);
      setEmailSubject(defaultSubject);
      setEmailBody('');
    }
  }, [allTemplates, defaultSubject, applyTemplate]);

  const handleSendEmail = useCallback(async () => {
    if (!inspectionId || !emailTo) return;
    setEmailSending(true);
    try {
      await inspectionApi.emailReport(inspectionId, {
        to: emailTo,
        subject: emailSubject || undefined,
        template_body: emailBody || undefined,
      });
      setEmailSent(true);
      setTimeout(() => { setEmailOpen(false); setEmailSent(false); }, 2000);
    } catch {
      // stop loading
    } finally {
      setEmailSending(false);
    }
  }, [inspectionId, emailTo, emailSubject, emailBody]);

  // Expose actions to parent for unified bottom bar
  if (actionsRef) {
    actionsRef.current = { handleShare, handlePrint, openEmailModal, openSmsModal, copied, hasReport: !!portalUrl };
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%', background: '#18181B', position: 'relative' }}>
      {onClose && <CloseTab onClick={onClose} />}
      {/* Header */}
      <Box style={{ padding: '10px 16px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: '#1E1E21' }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text size="11px" fw={700} c="#E4E4E7" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>Report Preview</Text>
          {updating && <Loader size={12} />}
        </Box>
        <Tooltip label="Regenerate report" position="left" withArrow>
          <ActionIcon variant="subtle" size={28} radius="xl" onClick={() => mutate()} style={{ color: '#71717A' }}>
            <IconRefresh size={15} />
          </ActionIcon>
        </Tooltip>
      </Box>

      {/* Report view */}
      <Box style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#F5F5F5' }}>
        {error ? (
          <Center h="100%">
            <Text c="red" size="sm">Failed to load report preview</Text>
          </Center>
        ) : !portalUrl ? (
          <Center h="100%">
            <Stack align="center" gap={12}>
              <Box style={{
                width: 48, height: 48, borderRadius: 12,
                background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IconFileText size={22} color="#3B82F6" style={{ opacity: 0.6 }} />
              </Box>
              <Loader size="sm" color="blue" type="dots" />
              <Text size="xs" c="#71717A">Loading report...</Text>
            </Stack>
          </Center>
        ) : (
          <>
            <iframe
              ref={iframeRef}
              key={refreshKey}
              src={portalUrl || undefined}
              title="Report Preview"
              style={{ width: '100%', height: '100%', border: 'none', background: '#F5F5F5' }}
            />
            {/* Transparent overlay to block iframe pointer events during drag */}
            <div ref={iframeOverlayRef} style={{ display: 'none', position: 'absolute', inset: 0, zIndex: 5, cursor: 'row-resize' }} />
            {updating && (
              <Center style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <Box style={{
                  padding: '12px 20px', borderRadius: 10,
                  background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <Loader size={16} color="blue" />
                  <Text size="xs" c="#fff" fw={500}>Updating...</Text>
                </Box>
              </Center>
            )}
          </>
        )}
      </Box>

      {/* Report action buttons — bottom bar (hidden when parent renders them) */}
      {portalUrl && !hideActionBar && (
        <Box style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: `1px solid #27272A`, background: '#18181B', flexShrink: 0 }}>
          <UnstyledButton onClick={handleShare} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 0', borderRadius: 8, background: '#27272A', border: '1px solid #333' }}>
            <IconLink size={20} color={copied ? '#22C55E' : '#A1A1AA'} />
            <Text size="xs" c={copied ? '#22C55E' : '#A1A1AA'} fw={600}>{copied ? 'Copied!' : 'Link'}</Text>
          </UnstyledButton>
          <UnstyledButton onClick={handlePrint} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 0', borderRadius: 8, background: '#27272A', border: '1px solid #333' }}>
            <IconPrinter size={20} color="#A1A1AA" />
            <Text size="xs" c="#A1A1AA" fw={600}>Print</Text>
          </UnstyledButton>
          <UnstyledButton onClick={openEmailModal} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 0', borderRadius: 8, background: '#27272A', border: '1px solid #333' }}>
            <IconMail size={20} color="#A1A1AA" />
            <Text size="xs" c="#A1A1AA" fw={600}>Email</Text>
          </UnstyledButton>
          <UnstyledButton onClick={openSmsModal} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 0', borderRadius: 8, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}>
            <IconMessage size={20} color="#22C55E" />
            <Text size="xs" c="#22C55E" fw={600}>SMS</Text>
          </UnstyledButton>
        </Box>
      )}

      {/* Email Drawer */}
      <Drawer
        opened={emailOpen}
        onClose={() => setEmailOpen(false)}
        title="Email Report"
        position="right"
        size="md"
        styles={{
          header: { background: '#1E1E21', borderBottom: `1px solid ${BORDER}`, padding: '12px 16px' },
          title: { color: '#fff', fontWeight: 600, fontSize: 14 },
          body: { background: '#1E1E21', padding: 16, display: 'flex', flexDirection: 'column', height: 'calc(100% - 52px)' },
          content: { background: '#1E1E21' },
          close: { color: '#A1A1AA' },
        }}
      >
        <Stack gap={12} style={{ flex: 1, minHeight: 0 }}>
          <Select
            label="Template"
            placeholder="Select a template"
            data={allTemplates.map((t) => ({ value: t.id, label: t.name }))}
            value={selectedTemplateId}
            onChange={onTemplateChange}
            clearable
            styles={modalSelectStyles}
            size="sm"
          />
          <TextInput
            label="To"
            placeholder="customer@email.com"
            value={emailTo}
            onChange={(e) => setEmailTo(e.currentTarget.value)}
            styles={modalInputStyles}
          />
          <TextInput
            label="Subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.currentTarget.value)}
            styles={modalInputStyles}
          />
          <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Textarea
              label="Message"
              value={emailBody}
              onChange={(e) => setEmailBody(e.currentTarget.value)}
              size="sm"
              minRows={8}
              autosize
              styles={{
                ...modalInputStyles,
                root: { flex: 1, display: 'flex', flexDirection: 'column' },
                wrapper: { flex: 1, display: 'flex', flexDirection: 'column' },
              }}
            />
            <Text size="xs" c="#52525B" mt={6}>
              A "View Report" button is automatically added to every email.
            </Text>
          </Box>
          <Button
            fullWidth
            mt={4}
            onClick={handleSendEmail}
            loading={emailSending}
            disabled={!emailTo || emailSent}
            color={emailSent ? 'green' : 'blue'}
            leftSection={emailSent ? <IconCheck size={16} /> : <IconSend size={16} />}
            style={{ flexShrink: 0 }}
          >
            {emailSent ? 'Sent!' : 'Send Email'}
          </Button>
        </Stack>
      </Drawer>

      {/* SMS Drawer */}
      <Drawer
        opened={smsOpen}
        onClose={() => setSmsOpen(false)}
        title="SMS Report"
        position="right"
        size="md"
        styles={{
          header: { background: '#1E1E21', borderBottom: `1px solid ${BORDER}`, padding: '12px 16px' },
          title: { color: '#fff', fontWeight: 600, fontSize: 14 },
          body: { background: '#1E1E21', padding: 16, display: 'flex', flexDirection: 'column', height: 'calc(100% - 52px)' },
          content: { background: '#1E1E21' },
          close: { color: '#A1A1AA' },
        }}
      >
        <Stack gap={12} style={{ flex: 1, minHeight: 0 }}>
          <TextInput
            label="To"
            placeholder="+1 (555) 123-4567"
            value={smsTo}
            onChange={(e) => setSmsTo(e.currentTarget.value)}
            styles={modalInputStyles}
          />
          <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Textarea
              label="Preview"
              value={smsBody}
              onChange={(e) => setSmsBody(e.currentTarget.value)}
              size="sm"
              minRows={4}
              autosize
              styles={{
                ...modalInputStyles,
                root: { flex: 1, display: 'flex', flexDirection: 'column' },
                wrapper: { flex: 1, display: 'flex', flexDirection: 'column' },
              }}
            />
            <Text size="xs" c="#52525B" mt={6}>
              The report link will be automatically included in the message.
            </Text>
          </Box>
          <Button
            fullWidth
            mt={4}
            onClick={handleSmsSend}
            loading={smsSending}
            disabled={!smsTo.trim() || smsSent}
            color={smsSent ? 'green' : 'teal'}
            leftSection={smsSent ? <IconCheck size={16} /> : <IconMessage size={16} />}
            style={{ flexShrink: 0 }}
          >
            {smsSent ? 'Sent!' : 'Send SMS'}
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
}
